import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  CreditTransactionDetailModal,
  FormField,
  PortalUsdLedgerSummaryGrid,
  PortalUsdWalletFiltersBar,
  PortalUsdWalletLedgerTable,
  SearchSelect,
  computeUsdLedgerSummary,
  formatUsdPlain,
  defaultUsdLedgerFilters,
  resolveUsdPeriodDateRange,
  validateUsdExportPeriod,
  usePortalLedgerDetailState,
  NumberedPagination,
  type UsdLedgerFilterState,
  type UsdLedgerRowLike,
} from '@kioskgaming/ui';
import useTableSort from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';
import { usePermission } from '../../../hooks/useAuth';
import {
  useCdnAgents,
  useSuperAgents,
  useUsdWalletLedger,
  useUsdWalletLedgerEntry,
} from '../../../hooks/useApi';
import { apiClient } from '../../../services/api';
import type { CdnAgent, SuperAgent, User } from '../../../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/Card';
import { Button } from '@/components/ui/shadcn';
import AccessDeniedPanel from '../../ui/AccessDeniedPanel';
import UsdWalletLedgerDetailModal from './UsdWalletLedgerDetailModal';
import { LoadingSpinner } from '../../ui/Loading';
import { cn, downloadBlob, parseErrorMessage } from '../../../utils';
import { ENV } from '../../../config/env';

type OwnerType = 'player' | 'shop' | 'agent' | 'super_agent' | 'platform';
type OwnerTypeFilter = 'all' | OwnerType;

const DEFAULT_PLATFORM_USD_OWNER_ID = '00000000-0000-4000-8000-000000000001';

const OWNER_TYPES: Array<{ value: OwnerTypeFilter; label: string }> = [
  { value: 'all', label: 'All (global)' },
  { value: 'player', label: 'Player' },
  { value: 'shop', label: 'Shop' },
  { value: 'agent', label: 'Agent' },
  { value: 'super_agent', label: 'Super agent' },
  { value: 'platform', label: 'Platform (house)' },
];

const OWNER_TYPE_SEARCH_OPTIONS = OWNER_TYPES.map((t) => ({
  value: t.value,
  label: t.label,
}));

const CURRENCY_USD = 'USD';
const PAGE_SIZE = 50;

type OwnerPickerOption = {
  id: string;
  primary: string;
  secondary: string;
};

function normalizeSearch(q: string): string {
  return q.trim().toLowerCase();
}

function matchesOwnerSearch(opt: OwnerPickerOption, q: string): boolean {
  const n = normalizeSearch(q);
  if (!n) return true;
  const hay = `${opt.primary} ${opt.secondary} ${opt.id}`.toLowerCase();
  return hay.includes(n);
}

function userToOwnerOption(u: User): OwnerPickerOption {
  const phone = (u.phone || '').trim();
  const email = (u.email || '').trim();
  const first = u.profile?.firstName?.trim();
  const last = u.profile?.lastName?.trim();
  const fullName = [first, last].filter(Boolean).join(' ').trim();
  const uname = (u.userName || u.name || '').trim();
  const primary = phone || email || fullName || uname || u.id;
  let secondary = '';
  if (phone) {
    secondary = [fullName || uname, email].filter(Boolean).join(' · ') || email || u.id;
  } else if (email) {
    secondary = [fullName || uname, u.id !== primary ? u.id : ''].filter(Boolean).join(' · ');
  } else {
    secondary = u.id !== primary ? u.id : '';
  }
  if (secondary === primary) secondary = '';
  return { id: u.id, primary, secondary };
}

function agentToOwnerOption(a: CdnAgent): OwnerPickerOption {
  const phone = (a.phone || '').trim();
  const secondary = [a.email, phone].filter(Boolean).join(' · ');
  return { id: a.id, primary: a.name || a.email || a.id, secondary };
}

function superAgentToOwnerOption(sa: SuperAgent): OwnerPickerOption {
  const phone = (sa.phone || '').trim();
  const secondary = [sa.email, phone].filter(Boolean).join(' · ');
  return { id: sa.id, primary: sa.name || sa.email || sa.id, secondary };
}

function rowToUsdRow(r: Record<string, unknown>, fallbackIndex = 0): UsdLedgerRowLike {
  return {
    ...r,
    id: r.id != null && String(r.id) !== '' ? String(r.id) : `row-${fallbackIndex}`,
  } as UsdLedgerRowLike;
}

function rowMatchesDeepLink(r: UsdLedgerRowLike, ledgerId: string, tx: string): boolean {
  const rowId = String(r.id ?? '').trim();
  const rowTx = String(r.transactionId ?? r.transaction_id ?? '').trim();
  if (ledgerId && rowId === ledgerId) return true;
  if (tx && rowTx === tx) return true;
  return false;
}

function initialFilters(hasDeepLink: boolean): UsdLedgerFilterState {
  const base = defaultUsdLedgerFilters();
  if (hasDeepLink) return base;
  const { dateFrom, dateTo } = resolveUsdPeriodDateRange('30d', '', '');
  return { ...base, period: '30d', dateFrom, dateTo };
}

const UsdWalletsManagement: React.FC = () => {
  const canView = usePermission('walletManagement');
  const [searchParams] = useSearchParams();
  const ledgerIdFromUrl = (searchParams.get('ledgerId') || '').trim();
  const txFromUrl = (searchParams.get('tx') || '').trim();
  const ownerTypeFromUrl = (searchParams.get('ownerType') || '').trim().toLowerCase();
  const ownerIdFromUrl = (searchParams.get('ownerId') || '').trim();
  const hasDeepLink = Boolean(ledgerIdFromUrl || txFromUrl);

  const [ownerType, setOwnerType] = useState<OwnerTypeFilter>(() => {
    if (
      ownerTypeFromUrl &&
      ['player', 'shop', 'agent', 'super_agent', 'platform'].includes(ownerTypeFromUrl)
    ) {
      return ownerTypeFromUrl as OwnerType;
    }
    return 'all';
  });
  const [ownerId, setOwnerId] = useState<string>(() => ownerIdFromUrl || '');
  const [comboQuery, setComboQuery] = useState('');
  const [debouncedPlayerSearch, setDebouncedPlayerSearch] = useState('');
  const [draft, setDraft] = useState<UsdLedgerFilterState>(() => initialFilters(hasDeepLink));
  const [applied, setApplied] = useState<UsdLedgerFilterState>(() => initialFilters(hasDeepLink));
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const deepLinkResolvedRef = useRef(false);

  const tableSort = useTableSort({
    columns: getTableSortColumns('usdWallets'),
    onChange: () => setPage(1),
  });

  const offset = (page - 1) * PAGE_SIZE;

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedPlayerSearch(comboQuery), 320);
    return () => window.clearTimeout(t);
  }, [comboQuery]);

  const prevOwnerTypeRef = useRef<OwnerTypeFilter>(ownerType);
  useEffect(() => {
    const prev = prevOwnerTypeRef.current;
    prevOwnerTypeRef.current = ownerType;
    if (prev === ownerType) return;
    setComboQuery('');
    setPage(1);
    if (ownerType === 'platform') {
      const fromEnv = String(ENV.PLATFORM_USD_OWNER_ID || '').trim();
      setOwnerId(fromEnv || DEFAULT_PLATFORM_USD_OWNER_ID);
    } else {
      setOwnerId('');
    }
  }, [ownerType]);

  const enabledLedger = canView && (ownerType === 'all' || ownerId.trim().length > 0);

  const { data: agentsRes, isLoading: agentsLoading } = useCdnAgents(canView);
  const { data: superAgentsRes, isLoading: superAgentsLoading } = useSuperAgents(canView);

  const { data: shopsMetaRes, isLoading: shopsLoading } = useQuery(
    ['usdWalletShopsMeta'],
    () => apiClient.getAdminInAppPopupShopsMeta({ limit: 2000 }),
    { enabled: canView, staleTime: 60 * 1000 },
  );

  const { data: usersRes, isLoading: usersLoading } = useQuery(
    ['usdWalletPlayers', debouncedPlayerSearch],
    () =>
      apiClient.getUsers({
        page: 1,
        limit: 100,
        search: debouncedPlayerSearch.trim() || undefined,
      }),
    { enabled: canView && ownerType === 'player', keepPreviousData: true, staleTime: 20 * 1000 },
  );

  const staticOptions = useMemo((): OwnerPickerOption[] => {
    if (ownerType === 'platform') {
      const id = String(ENV.PLATFORM_USD_OWNER_ID || '').trim() || DEFAULT_PLATFORM_USD_OWNER_ID;
      return [{ id, primary: 'Platform payout pool', secondary: 'Players without shop (house tier)' }];
    }
    if (ownerType === 'agent') return (agentsRes?.data ?? []).map(agentToOwnerOption);
    if (ownerType === 'super_agent') return (superAgentsRes?.data ?? []).map(superAgentToOwnerOption);
    if (ownerType === 'shop') {
      return (shopsMetaRes?.data?.shops ?? []).map((s) => ({
        id: s.id,
        primary: s.name,
        secondary: [s.agentName || 'Agent', s.email].filter(Boolean).join(' · '),
      }));
    }
    return [];
  }, [ownerType, agentsRes, superAgentsRes, shopsMetaRes]);

  const playerOptions = useMemo(
    () => (usersRes?.data?.users ?? []).map(userToOwnerOption),
    [usersRes],
  );

  const allOwnerOptions = useMemo(() => {
    if (ownerType === 'player') return playerOptions;
    return staticOptions;
  }, [ownerType, playerOptions, staticOptions]);

  const filteredOwnerOptions = useMemo(() => {
    if (ownerType === 'player') {
      return playerOptions.filter((o) => matchesOwnerSearch(o, comboQuery));
    }
    return staticOptions.filter((o) => matchesOwnerSearch(o, comboQuery)).slice(0, 200);
  }, [ownerType, playerOptions, staticOptions, comboQuery]);

  const ownerSearchSelectOptions = useMemo(() => {
    if (!ownerId) return filteredOwnerOptions.map((o) => ({ value: o.id, label: o.primary, sub: o.secondary || undefined }));
    const selected = allOwnerOptions.find((o) => o.id === ownerId);
    const base = filteredOwnerOptions.map((o) => ({ value: o.id, label: o.primary, sub: o.secondary || undefined }));
    if (!selected || base.some((o) => o.value === ownerId)) return base;
    return [{ value: selected.id, label: selected.primary, sub: selected.secondary || undefined }, ...base];
  }, [filteredOwnerOptions, ownerId, allOwnerOptions]);

  const listLoading =
    (ownerType === 'agent' && agentsLoading) ||
    (ownerType === 'super_agent' && superAgentsLoading) ||
    (ownerType === 'shop' && shopsLoading) ||
    (ownerType === 'player' && usersLoading);

  const appliedDates = useMemo(
    () => resolveUsdPeriodDateRange(applied.period, applied.dateFrom, applied.dateTo),
    [applied.period, applied.dateFrom, applied.dateTo],
  );

  const ledgerQuery = useUsdWalletLedger(
    {
      ...(ownerType === 'all' ? {} : { ownerType, ownerId: ownerId.trim() }),
      currency: CURRENCY_USD,
      ...(applied.typeFilter !== 'all' ? { type: applied.typeFilter } : {}),
      ...(applied.statusFilter !== 'all' ? { status: applied.statusFilter } : {}),
      ...(applied.query.trim() ? { query: applied.query.trim() } : {}),
      dateFrom: appliedDates.dateFrom || undefined,
      dateTo: appliedDates.dateTo || undefined,
      limit: PAGE_SIZE,
      offset,
      ...tableSort.sortParams,
    },
    enabledLedger,
  );

  const deepLinkEntryQuery = useUsdWalletLedgerEntry(
    ledgerIdFromUrl,
    canView && hasDeepLink && !!ledgerIdFromUrl,
  );

  const deepLinkTxQuery = useUsdWalletLedger(
    {
      ...(ownerType === 'all' ? {} : { ownerType, ownerId: ownerId.trim() }),
      currency: CURRENCY_USD,
      transactionId: txFromUrl,
      limit: 1,
      offset: 0,
    },
    enabledLedger && hasDeepLink && !ledgerIdFromUrl && !!txFromUrl,
  );

  const tableRows = useMemo(
    () => (ledgerQuery.data?.rows ?? []).map((r, i) => rowToUsdRow(r as Record<string, unknown>, i)),
    [ledgerQuery.data?.rows],
  );

  const appliedSort = useMemo(
    () => ({
      sortBy: ledgerQuery.data?.sortBy ?? null,
      sortOrder: (ledgerQuery.data?.sortOrder ?? null) as SortDirection | null,
    }),
    [ledgerQuery.data?.sortBy, ledgerQuery.data?.sortOrder],
  );

  const headerSort = tableSort.sortBy
    ? {
        activeKey: appliedSort.sortBy ?? tableSort.sortBy,
        direction: appliedSort.sortOrder ?? tableSort.sortOrder,
      }
    : { activeKey: null, direction: null };

  const summary = useMemo(() => {
    const computed = computeUsdLedgerSummary(tableRows);
    if (ledgerQuery.data?.count != null) {
      return { ...computed, totalTransactions: ledgerQuery.data.count };
    }
    return computed;
  }, [tableRows, ledgerQuery.data?.count]);

  const count = ledgerQuery.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const isGloballyEmpty = !ledgerQuery.isLoading && count === 0 && enabledLedger;

  const balanceNet = useMemo(() => {
    const fromLedger = ledgerQuery.data?.balanceNet;
    if (fromLedger != null && Number.isFinite(Number(fromLedger))) return Number(fromLedger);
    return 0;
  }, [ledgerQuery.data?.balanceNet]);

  const showOwnerBalance = ownerType !== 'all' && Boolean(ownerId.trim());

  const ledgerDetail = usePortalLedgerDetailState({
    usdRows: tableRows,
    fetchCreditById: async (id) => {
      const res = await apiClient.getCreditWalletLedgerEntry(id);
      return (res.item ?? {}) as Record<string, unknown>;
    },
    fetchUsdById: async (id) => {
      const res = await apiClient.getUsdWalletLedgerEntry(id);
      return (res.row ?? {}) as UsdLedgerRowLike;
    },
    onUnavailable: () =>
      toast.error('Related transaction is unavailable or outside your permission.'),
  });

  const resetFilters = () => {
    const next = initialFilters(false);
    setDraft(next);
    setApplied(next);
    setPage(1);
  };

  const handleApplyFilters = () => {
    setApplied(draft);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [enabledLedger, ownerType, ownerId]);

  const refresh = useCallback(async () => {
    if (!enabledLedger) return;
    await ledgerQuery.refetch();
  }, [enabledLedger, ledgerQuery]);

  const handleExport = useCallback(async () => {
    const dates = resolveUsdPeriodDateRange(applied.period, applied.dateFrom, applied.dateTo);
    const periodErr = validateUsdExportPeriod(dates.dateFrom, dates.dateTo);
    if (periodErr) {
      toast.error(periodErr);
      return;
    }
    if (!enabledLedger) {
      toast.error('Select owner filters before exporting.');
      return;
    }

    setExporting(true);
    try {
      const { blob, filename } = await apiClient.exportUsdWalletLedger({
        ...(ownerType === 'all' ? {} : { ownerType, ownerId: ownerId.trim() }),
        currency: CURRENCY_USD,
        ...(applied.typeFilter !== 'all' ? { type: applied.typeFilter } : {}),
        ...(applied.statusFilter !== 'all' ? { status: applied.statusFilter } : {}),
        ...(applied.query.trim() ? { query: applied.query.trim() } : {}),
        dateFrom: dates.dateFrom,
        dateTo: dates.dateTo,
      });
      downloadBlob(blob, filename);
      toast.success('USD wallet ledger exported');
    } catch (err) {
      toast.error(parseErrorMessage(err) || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [applied, enabledLedger, ownerType, ownerId]);

  useEffect(() => {
    if (!hasDeepLink || !enabledLedger || deepLinkResolvedRef.current) return;

    const listReady = !ledgerQuery.isLoading && !ledgerQuery.isFetching;
    const entryReady =
      !ledgerIdFromUrl || (!deepLinkEntryQuery.isLoading && !deepLinkEntryQuery.isFetching);
    const txReady =
      !!ledgerIdFromUrl || !txFromUrl || (!deepLinkTxQuery.isLoading && !deepLinkTxQuery.isFetching);
    if (!listReady || !entryReady || !txReady) return;

    const fromList = tableRows.find((r) => rowMatchesDeepLink(r, ledgerIdFromUrl, txFromUrl));
    if (fromList) {
      ledgerDetail.openUsd(fromList);
      deepLinkResolvedRef.current = true;
      return;
    }

    const entryRow = deepLinkEntryQuery.data?.row;
    if (entryRow && typeof entryRow === 'object') {
      const normalized = rowToUsdRow(entryRow as Record<string, unknown>);
      if (rowMatchesDeepLink(normalized, ledgerIdFromUrl, txFromUrl)) {
        ledgerDetail.openUsd(normalized);
        deepLinkResolvedRef.current = true;
        return;
      }
    }

    const txRow = deepLinkTxQuery.data?.rows?.[0];
    if (txRow && typeof txRow === 'object') {
      const normalized = rowToUsdRow(txRow as Record<string, unknown>);
      if (rowMatchesDeepLink(normalized, ledgerIdFromUrl, txFromUrl)) {
        ledgerDetail.openUsd(normalized);
        deepLinkResolvedRef.current = true;
        return;
      }
    }

    if (ledgerIdFromUrl) {
      void ledgerDetail.openUsdById(ledgerIdFromUrl);
    } else if (txFromUrl) {
      toast.error('Transaction not found in the current list. Adjust filters or owner scope.');
    }
    deepLinkResolvedRef.current = true;
  }, [
    hasDeepLink,
    enabledLedger,
    ledgerIdFromUrl,
    txFromUrl,
    tableRows,
    ledgerQuery.isLoading,
    ledgerQuery.isFetching,
    deepLinkEntryQuery.data,
    deepLinkEntryQuery.isLoading,
    deepLinkEntryQuery.isFetching,
    deepLinkTxQuery.data,
    deepLinkTxQuery.isLoading,
    deepLinkTxQuery.isFetching,
    ledgerDetail,
  ]);

  if (!canView) {
    return <AccessDeniedPanel message="You don't have permission to view USD wallets." />;
  }

  const isLoading = ledgerQuery.isLoading;
  const isRefreshing = ledgerQuery.isFetching && !ledgerQuery.isLoading;
  const showLedgerLoading = isLoading || isRefreshing;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col gap-4 space-y-0 border-b border-border pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-xl">USD wallet transactions</CardTitle>
            <CardDescription>
              Review USD wallet activity across the platform. Optionally narrow to a single owner.
            </CardDescription>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleExport()}
              disabled={!enabledLedger || exporting || showLedgerLoading}
            >
              <Download className={cn('mr-2 h-4 w-4', exporting && 'animate-pulse')} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void refresh()}
              disabled={!enabledLedger || showLedgerLoading}
            >
              <RefreshCw className={cn('mr-2 h-4 w-4', showLedgerLoading && 'animate-spin')} />
              {isRefreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-0 p-0">
          <div className="space-y-4 border-b border-border px-6 py-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-start">
              <FormField label="Owner type" controlId="usd-owner-type">
                <SearchSelect
                  variant="dropdown"
                  placeholder="Owner type"
                  searchPlaceholder="Search owner type…"
                  options={OWNER_TYPE_SEARCH_OPTIONS}
                  value={ownerType}
                  onChange={(v) => {
                    const next = typeof v === 'string' ? v : '';
                    if (next && OWNER_TYPES.some((t) => t.value === next)) {
                      setOwnerType(next as OwnerTypeFilter);
                    }
                  }}
                  size="sm"
                  emptyMessage="No matches."
                />
              </FormField>

              <div className="min-w-0 md:col-span-2">
                {ownerType !== 'all' &&
                  (listLoading && ownerType !== 'player' ? (
                    <p className="text-sm text-muted-foreground">Loading list…</p>
                  ) : (
                    <FormField label="Owner" controlId="usd-owner-searchselect">
                      <SearchSelect
                        variant="dropdown"
                        placeholder={
                          ownerType === 'player' && usersLoading ? 'Loading players…' : 'Select owner'
                        }
                        searchPlaceholder={
                          ownerType === 'player'
                            ? 'Search by phone, email, name…'
                            : 'Search by name, phone, email…'
                        }
                        options={ownerSearchSelectOptions}
                        value={ownerId}
                        onChange={(v) => {
                          setOwnerId(typeof v === 'string' ? v : '');
                          setPage(1);
                        }}
                        filterValue={comboQuery}
                        onFilterChange={setComboQuery}
                        disabled={
                          (ownerType === 'player' && usersLoading) ||
                          (ownerType !== 'player' && listLoading)
                        }
                        size="sm"
                        emptyMessage="No matches."
                      />
                    </FormField>
                  ))}
              </div>
            </div>
          </div>

          {!enabledLedger ? (
            <p className="px-6 py-10 text-sm text-muted-foreground">
              Select an owner type and owner to load USD transactions, or choose &quot;All (global)&quot; for the
              platform-wide ledger.
            </p>
          ) : ledgerQuery.error ? (
            <p className="px-6 py-10 text-sm text-destructive">Failed to load USD wallet data.</p>
          ) : showLedgerLoading && tableRows.length === 0 ? (
            <div className="flex h-48 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-0">
              {showOwnerBalance ? (
                <div className="border-b border-border px-6 py-5">
                  <div className="max-w-sm rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Current USD balance
                    </p>
                    <p className="mt-2 text-2xl font-bold tabular-nums text-gray-900">
                      {formatUsdPlain(balanceNet)}
                    </p>
                    <p className="mt-2 text-sm text-gray-500">Net balance for selected owner</p>
                  </div>
                </div>
              ) : null}

              <div className="overflow-hidden">
                <div className="border-b border-gray-100 px-6 py-4">
                  <PortalUsdLedgerSummaryGrid summary={summary} loading={showLedgerLoading} />
                </div>
                <PortalUsdWalletFiltersBar
                  draft={draft}
                  onDraftChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
                  onApply={handleApplyFilters}
                  onReset={resetFilters}
                />
                <PortalUsdWalletLedgerTable
                  rows={tableRows}
                  loading={showLedgerLoading}
                  isGloballyEmpty={isGloballyEmpty}
                  onViewDetail={(row) => ledgerDetail.openUsd(row)}
                  sortable
                  sortActiveKey={headerSort.activeKey}
                  sortDirection={headerSort.direction}
                  onSort={tableSort.toggleSort}
                />
                {totalPages > 1 ? (
                  <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 sm:flex-row">
                    <p className="text-sm text-gray-500">
                      Page {page} of {totalPages} · {count} total
                    </p>
                    <NumberedPagination
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                      disabled={showLedgerLoading}
                      variant="admin"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CreditTransactionDetailModal
        entry={ledgerDetail.creditDetail}
        onClose={ledgerDetail.closeCredit}
        accountLabel="Credit ledger"
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
        resolveUsdConversion={ledgerDetail.resolveUsdConversion}
      />
      <UsdWalletLedgerDetailModal row={ledgerDetail.usdDetail} onClose={ledgerDetail.closeUsd} />
    </>
  );
};

export default UsdWalletsManagement;
